import { Add, Delete } from '@mui/icons-material';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  GridActionsCellItem,
  GridActionsCellItemProps,
  GridColDef,
  GridValidRowModel,
} from '@mui/x-data-grid';
import { ReactElement, useMemo, useState } from 'react';
import ProgramFilter from '../../../components/filters/ProgramFilter';
import ProjectFilter from '../../../components/filters/ProjectFilter';
import { StyledDataGrid } from '../../../shared-theme/StyledComponents';

// Generic Pattern B join panel: parent (Program/Project) selector + DataGrid of
// child rows assigned to that parent + Add (dropdown of unassigned children) +
// Remove. Used by ProgramLocation, ProgramObjectType, ProgramWorkflowType, and
// ProjectCLIN. Each consumer wires the RTK list/add/remove hooks plus a child
// catalog hook for the Add dropdown.

export type ParentType = 'program' | 'project';

export interface AdminPatternBJoinPanelProps<
  TJoinRow extends GridValidRowModel = GridValidRowModel,
  TChild extends GridValidRowModel = GridValidRowModel,
> {
  tableName: string;
  apiPath: string;
  parentType: ParentType;

  /* eslint-disable @typescript-eslint/no-explicit-any */
  /** RTK list hook for join rows under the given parentId. Should support `skip` when no parent selected. */
  useList: (parentId: string) => {
    data?: TJoinRow[] | { items?: TJoinRow[] };
    isLoading?: boolean;
    isFetching?: boolean;
    refetch?: () => unknown;
  };
  useAdd: () => readonly [(arg: any) => any, { isLoading: boolean }];
  useRemove: () => readonly [(arg: any) => any, { isLoading: boolean }];

  buildAddArg: (parentId: string, child: TChild) => unknown;
  buildRemoveArg: (parentId: string, row: TJoinRow) => unknown;

  /** RTK list hook for the child catalog used by the Add dropdown. */
  useChildList: () => {
    data?: TChild[] | { items?: TChild[] };
    isLoading?: boolean;
  };
  /* eslint-enable @typescript-eslint/no-explicit-any */

  /** Field on a child item that holds its PK (e.g., "locationId" / "objectType"). */
  childPkAccessor: keyof TChild & string;
  /** Field on a child item used as the dropdown label (e.g., "name"). */
  childLabelAccessor: keyof TChild & string;
  /** Field on a join row that maps to the child's PK (used to compute unassigned). */
  joinChildIdAccessor: keyof TJoinRow & string;

  columns: GridColDef<TJoinRow>[];
}

interface SnackState {
  msg: string;
  severity: 'success' | 'error' | 'info';
}

function AdminPatternBJoinPanel<
  TJoinRow extends GridValidRowModel = GridValidRowModel,
  TChild extends GridValidRowModel = GridValidRowModel,
>({
  tableName,
  apiPath,
  parentType,
  useList,
  useAdd,
  useRemove,
  buildAddArg,
  buildRemoveArg,
  useChildList,
  childPkAccessor,
  childLabelAccessor,
  joinChildIdAccessor,
  columns,
}: AdminPatternBJoinPanelProps<TJoinRow, TChild>) {
  const [parentId, setParentId] = useState<string>('');
  const listResult = useList(parentId);
  const childResult = useChildList();

  const [addMutation, { isLoading: adding }] = useAdd();
  const [removeMutation, { isLoading: removing }] = useRemove();

  const [addOpen, setAddOpen] = useState(false);
  const [selectedChild, setSelectedChild] = useState<TChild | null>(null);
  const [confirmRow, setConfirmRow] = useState<TJoinRow | null>(null);
  const [snack, setSnack] = useState<SnackState | null>(null);

  const rows: TJoinRow[] = useMemo(() => {
    const data = listResult?.data;
    if (!data) return [];
    if (Array.isArray(data)) return data;
    return data.items ?? [];
  }, [listResult?.data]);

  const allChildren: TChild[] = useMemo(() => {
    const data = childResult?.data;
    if (!data) return [];
    if (Array.isArray(data)) return data;
    return data.items ?? [];
  }, [childResult?.data]);

  const availableChildren = useMemo(() => {
    const assigned = new Set(
      rows.map((r) => (r as Record<string, unknown>)[joinChildIdAccessor]),
    );
    return allChildren.filter(
      (c) => !assigned.has((c as Record<string, unknown>)[childPkAccessor]),
    );
  }, [rows, allChildren, joinChildIdAccessor, childPkAccessor]);

  const handleAdd = async () => {
    if (!parentId || !selectedChild) return;
    try {
      await addMutation(buildAddArg(parentId, selectedChild)).unwrap();
      setSnack({ msg: `${tableName} added.`, severity: 'success' });
      listResult?.refetch?.();
      setAddOpen(false);
      setSelectedChild(null);
    } catch (e: unknown) {
      setSnack({
        msg: extractErrorMessage(e) ?? 'Add failed.',
        severity: 'error',
      });
    }
  };

  const handleRemove = async () => {
    if (!confirmRow || !parentId) return;
    try {
      await removeMutation(buildRemoveArg(parentId, confirmRow)).unwrap();
      setSnack({ msg: `${tableName} removed.`, severity: 'success' });
      listResult?.refetch?.();
    } catch (e: unknown) {
      setSnack({
        msg: extractErrorMessage(e) ?? 'Remove failed.',
        severity: 'error',
      });
    } finally {
      setConfirmRow(null);
    }
  };

  const fullColumns: GridColDef<TJoinRow>[] = useMemo(() => {
    const actionCol: GridColDef<TJoinRow> = {
      field: 'actions',
      type: 'actions',
      headerName: '',
      width: 80,
      getActions: ({ row }) => {
        const out: ReactElement<GridActionsCellItemProps>[] = [];
        out.push(
          <GridActionsCellItem
            key="remove"
            icon={<Delete />}
            label="Remove"
            onClick={() => setConfirmRow(row)}
          />,
        );
        return out;
      },
    };
    return [...columns, actionCol];
  }, [columns]);

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="caption" color="text.secondary">
        Table: {tableName} · API: {apiPath}
      </Typography>

      <Stack direction="row" spacing={2} alignItems="center">
        <Box sx={{ minWidth: 280, flex: 1 }}>
          {parentType === 'program' ? (
            <ProgramFilter
              programId={parentId}
              handleChange={(id) => setParentId(id ?? '')}
            />
          ) : (
            <ProjectFilter
              projectId={parentId}
              handleChange={(id) => setParentId(id ?? '')}
            />
          )}
        </Box>
        <Button
          variant="contained"
          size="small"
          startIcon={<Add />}
          onClick={() => setAddOpen(true)}
          disabled={!parentId || availableChildren.length === 0 || adding}
        >
          Add {tableName}
        </Button>
      </Stack>

      <Box sx={{ height: 380, width: '100%' }}>
        <StyledDataGrid
          rows={rows}
          columns={fullColumns as GridColDef[]}
          getRowId={(row) =>
            String(
              (row as Record<string, unknown>)[joinChildIdAccessor]
                ?? Math.random(),
            )
          }
          loading={
            !!(
              listResult?.isLoading
              || listResult?.isFetching
              || adding
              || removing
            )
          }
          disableRowSelectionOnClick
          density="compact"
        />
      </Box>

      <Dialog
        open={addOpen}
        onClose={() => {
          setAddOpen(false);
          setSelectedChild(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Assign {tableName}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Autocomplete
              options={availableChildren}
              getOptionLabel={(option) =>
                String(
                  (option as Record<string, unknown>)[childLabelAccessor] ?? '',
                )
              }
              value={selectedChild}
              onChange={(_, v) => setSelectedChild(v ?? null)}
              loading={!!childResult?.isLoading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={`Available ${tableName}s`}
                  size="small"
                  required
                />
              )}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setAddOpen(false);
              setSelectedChild(null);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAdd}
            disabled={!selectedChild || adding}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!confirmRow} onClose={() => setConfirmRow(null)}>
        <DialogTitle>Remove {tableName}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Remove this {tableName.toLowerCase()} from the selected{' '}
            {parentType}? The underlying record is not deleted.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRow(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleRemove}
            disabled={removing}
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!snack}
        autoHideDuration={4000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {snack ? (
          <Alert
            severity={snack.severity}
            variant="filled"
            onClose={() => setSnack(null)}
          >
            {snack.msg}
          </Alert>
        ) : (
          <span />
        )}
      </Snackbar>
    </Box>
  );
}

function extractErrorMessage(e: unknown): string | undefined {
  if (!e || typeof e !== 'object') return undefined;
  const data = (e as { data?: unknown }).data;
  if (data && typeof data === 'object') {
    const msg = data as { message?: string; error?: string; details?: string };
    return msg.message ?? msg.error ?? msg.details;
  }
  return undefined;
}

export default AdminPatternBJoinPanel;
